import { z } from 'zod'
import { Address, BlockNumber, ChainId, DecimalString, Hash32, HexData, IsoTimestamp, UsdString, Uuid, WeiString } from '../primitives.js'
import { EpochStatus, FeeCategory, TokenStatus } from '../enums.js'
import { AgentSummary } from './common.js'
import { DEFAULT_FEE_SPLIT } from '../constants.js'

/** Fee split in basis points; must sum to 10 000 (docs/CONTRACTS.md). */
export const FeePolicy = z
  .object({
    holderBps: z.number().int().min(0).max(10_000),
    creatorBps: z.number().int().min(0).max(10_000),
    platformBps: z.number().int().min(0).max(10_000),
    version: z.number().int().nonnegative(),
  })
  .refine((p) => p.holderBps + p.creatorBps + p.platformBps === 10_000, {
    message: 'holderBps + creatorBps + platformBps must equal 10000',
  })
export type FeePolicy = z.output<typeof FeePolicy>

/** Decided default split (docs/ECONOMICS.md): holders 40 %, creator 40 %, platform 20 %. */
export const DEFAULT_FEE_POLICY: FeePolicy = { ...DEFAULT_FEE_SPLIT, version: 1 }

export const DistributionEpoch = z.object({
  id: Uuid,
  agentId: Uuid,
  epochIndex: z.number().int().nonnegative(),
  startBlock: BlockNumber,
  endBlock: BlockNumber.nullable(),
  snapshotBlock: BlockNumber.nullable(),
  rewardToken: Address,
  amountWei: WeiString,
  fundedTxHash: Hash32.nullable(),
  status: EpochStatus,
  closedAt: IsoTimestamp.nullable(),
})
export type DistributionEpoch = z.output<typeof DistributionEpoch>

export const AgentTokenCard = z.object({
  agent: AgentSummary,
  chainId: ChainId,
  address: Address.nullable(),
  symbol: z.string(),
  name: z.string(),
  status: TokenStatus,
  holdersCount: z.number().int().nonnegative(),
  volume24hUsd: UsdString.nullable(),
  /** Holder distributions of closed epochs in ETH and WETH only (wei of ETH); other reward tokens are in `distributed`. */
  totalDistributedWei: WeiString,
  /**
   * Holder distributions of closed epochs per reward token, sorted by token address (amounts in that token's base
   * units, never added across tokens). The API always sends it; optional only until the web test stub does.
   */
  distributed: z.array(z.object({ rewardToken: Address, amountWei: WeiString, epochs: z.number().int().nonnegative() })).optional(),
  launchedAt: IsoTimestamp.nullable(),
})
export type AgentTokenCard = z.output<typeof AgentTokenCard>

export const TokenHolder = z.object({
  address: Address,
  balance: WeiString,
  asOfBlock: BlockNumber,
})

/** Kind of a launch quote asset (`GET /quote-assets`). */
export const QuoteAssetKind = z.enum(['native', 'stable', 'stock', 'other'])
export type QuoteAssetKind = z.output<typeof QuoteAssetKind>

/** An allowlisted launch quote asset (QuoteAssetRegistry) with token metadata. Zero address = native ETH. */
export const QuoteAsset = z.object({
  address: Address,
  symbol: z.string(),
  decimals: z.number().int().min(0).max(36),
  kind: QuoteAssetKind,
})
export type QuoteAsset = z.output<typeof QuoteAsset>

/** Positive decimal string (`"0.00001"`, `"42"`). */
export const PositiveDecimalString = z
  .string()
  .regex(/^(0|[1-9][0-9]*)(\.[0-9]+)?$/, 'expected a positive decimal string')
  .refine((s) => /[1-9]/.test(s), 'must be greater than 0')

/** Width of the single-sided launch range above the starting price: 100x, 10 000x, 1 000 000x. */
export const LaunchTickRange = z.enum(['tight', 'standard', 'wide'])
export type LaunchTickRange = z.output<typeof LaunchTickRange>

/**
 * Live state of an agent token's Uniswap V4 launch pool (StateView). Prices are decimal strings in quote
 * units per 1 agent token; `marketCapInQuote` = price × total supply.
 */
export const AgentTokenPool = z.object({
  poolId: Hash32,
  quoteAsset: Address,
  quoteSymbol: z.string().nullable(),
  quoteDecimals: z.number().int().min(0).max(36).nullable(),
  tickLower: z.number().int().nullable(),
  tickUpper: z.number().int().nullable(),
  /** Pool LP fee in hundredths of a bip (10 000 = 1 %). */
  lpFee: z.number().int().nonnegative().nullable(),
  /** `null` when the chain could not be read (the indexed launch data is still shown). */
  sqrtPriceX96: WeiString.nullable(),
  tick: z.number().int().nullable(),
  /** Active (in-range) liquidity. */
  liquidity: WeiString.nullable(),
  priceInQuote: DecimalString.nullable(),
  marketCapInQuote: DecimalString.nullable(),
  /** Liquidity seeded at launch (`PoolSeeded`). */
  seededLiquidity: WeiString.nullable(),
  seededAt: IsoTimestamp.nullable(),
  /** Deep link to a swap interface for chain 4663, null when none is known. */
  swapUrl: z.string().url().nullable(),
})
export type AgentTokenPool = z.output<typeof AgentTokenPool>

export const AgentTokenDetail = AgentTokenCard.extend({
  decimals: z.number().int().min(0).max(36),
  totalSupply: WeiString,
  launchTxHash: Hash32.nullable(),
  feePolicy: FeePolicy,
  poolAddress: Address.nullable(),
  /** Uniswap V4 launch pool; null before `PoolSeeded` is indexed and for linked external tokens. */
  pool: AgentTokenPool.nullable(),
  latestEpoch: DistributionEpoch.nullable(),
  topHolders: z.array(TokenHolder),
  /**
   * The platform's holders of the token (lane ALGOBOO1, runtime `holders.sync`): its count, the block its ranking stands
   * as of, when algo.boo read it, and the platform's blocker while it cannot answer. Optional until every stub sends it.
   */
  holders: z
    .object({ count: z.number().int().nonnegative().nullable(), throughBlock: z.string().nullable(), checkedAt: IsoTimestamp.nullable(), unavailable: z.string().nullable() })
    .optional(),
  // `distributed` (per reward token) comes from AgentTokenCard; ETH and WETH are separate entries.
})
export type AgentTokenDetail = z.output<typeof AgentTokenDetail>

/** An unsigned transaction for the user's linked wallet to sign and send. */
export const UnsignedTx = z.object({
  chainId: ChainId,
  to: Address,
  data: HexData,
  value: WeiString,
  gas: WeiString.optional(),
})
export type UnsignedTx = z.output<typeof UnsignedTx>

/**
 * Fair launch (docs/CONTRACTS.md "Launch and liquidity"): the whole supply seeds a single-sided Uniswap V4
 * position against an allowlisted quote asset. `startingPrice` is quote units per 1 agent token.
 */
export const LaunchToken = z.object({
  name: z.string().min(1).max(40),
  symbol: z.string().regex(/^[A-Z0-9]{2,11}$/, 'symbol must be 2-11 upper-case letters or digits'),
  /** Total supply in base units (18 decimals). */
  supply: WeiString,
  quoteAsset: Address,
  startingPrice: PositiveDecimalString,
  tickRange: LaunchTickRange.optional(),
  /**
   * Informational only: the on-chain split is the registry's policy (default 40/40/20, changed through
   * `proposeFeePolicy`). When given it must be inside the registry bounds.
   */
  feePolicy: FeePolicy.optional(),
})
export type LaunchToken = z.input<typeof LaunchToken>

/** What the launch transaction will create (derived before signing). */
export const LaunchPlan = z.object({
  predictedToken: Address,
  poolId: Hash32,
  quoteAsset: Address,
  startingSqrtPriceX96: WeiString,
  tickLower: z.number().int(),
  tickUpper: z.number().int(),
  tickSpacing: z.number().int().positive(),
  lpFee: z.number().int().nonnegative(),
  agentIsCurrency0: z.boolean(),
  /** Starting price after snapping to the tick grid, quote units per token. */
  startingPrice: DecimalString,
  /** startingPrice × supply, in quote units. */
  impliedMarketCapInQuote: DecimalString,
})
export type LaunchPlan = z.output<typeof LaunchPlan>

/**
 * `POST /agents/:handle/token/launch` (lane ALGOBOO1): the platform launches the token (its operator account sends the
 * factory call; algo.boo holds no key). `tx` is the creation fee's `payCreation` when the agent was created free while
 * the platform could not launch, null when its fee is paid already; `launch` the plan the platform will send.
 */
export const LaunchTokenResult = z.object({ tx: UnsignedTx.nullable(), token: AgentTokenCard, launch: LaunchPlan })
export type LaunchTokenResult = z.output<typeof LaunchTokenResult>

export const LinkToken = z.object({ address: Address })
export type LinkToken = z.input<typeof LinkToken>

/** One row per (epoch, reward token): an epoch that funded two reward tokens has two rows. */
export const ClaimableEpoch = z.object({
  epochId: Uuid,
  epochIndex: z.number().int().nonnegative(),
  rewardToken: Address,
  amountWei: WeiString,
  claimed: z.boolean(),
  /** The chain read failed: `amountWei` is 0 but may not be; show "unavailable", not "nothing to claim". */
  unavailable: z.boolean().optional(),
})
/** Unclaimed total of one reward token (amounts of different tokens are never added together). */
export const ClaimableTotal = z.object({ rewardToken: Address, amountWei: WeiString })
export type ClaimableTotal = z.output<typeof ClaimableTotal>
export const Claimable = z.object({
  holder: Address,
  epochs: z.array(ClaimableEpoch),
  /**
   * Per reward token, sorted by token address; one entry per token that has an epoch row. The API always sends it;
   * optional only until the web test stub sends it (docs/DEVIATIONS.md "reconciliation").
   */
  totals: z.array(ClaimableTotal).optional(),
  /**
   * Deprecated: the unclaimed total when every epoch row has the same reward token (then equal to `totals[0]`),
   * `'0'` when rows span several reward tokens (use `totals`).
   */
  totalWei: WeiString,
})
export type Claimable = z.output<typeof Claimable>


// Holder earnings (`GET /tokens/:address/earnings`, lane P1) ---------------------------------------------

/**
 * Fees recorded in `billing.fee_ledger` for the token's agent, per (category, fee token). `holderEligible` mirrors
 * FeeRouter's `holderEligible`: execution and subscription fees are shared with holders, performance fees are not.
 * `amountUsd` is null when any row of the group had no USD price (unknown, never 0).
 */
export const TokenEarningsCollected = z.object({
  category: FeeCategory,
  holderEligible: z.boolean(),
  token: Address.nullable(),
  amountWei: WeiString,
  amountUsd: UsdString.nullable(),
  count: z.number().int().nonnegative(),
  /** Rows settled by a FeeRouter `FeeCollected` log (on chain). */
  reconciledCount: z.number().int().nonnegative(),
})
export type TokenEarningsCollected = z.output<typeof TokenEarningsCollected>

/** The fee policy applied to an amount: holders / creator / platform in the amount's base units (platform takes the dust). */
export const FeeAllocation = z.object({ holdersWei: WeiString, creatorWei: WeiString, platformWei: WeiString })
export type FeeAllocation = z.output<typeof FeeAllocation>

/**
 * One closed distribution epoch per reward token. `fundedWei` is what FeeDistributor holds for holders (read from the
 * chain at close; it includes any amount rolled into this epoch). `collected` is the holder-eligible fee ledger in the
 * reward token recorded while the epoch was open (by `collected_at` between the previous close and this one), and
 * `allocation` is the fee policy applied to it. `paidWei` sums the indexed `Claimed` logs.
 */
export const TokenEarningsEpoch = z.object({
  epochId: Uuid,
  epochIndex: z.number().int().nonnegative(),
  rewardToken: Address,
  status: EpochStatus,
  startBlock: BlockNumber,
  endBlock: BlockNumber.nullable(),
  snapshotBlock: BlockNumber.nullable(),
  closedAt: IsoTimestamp.nullable(),
  fundedWei: WeiString,
  collected: z.object({ executionWei: WeiString, subscriptionWei: WeiString }),
  allocation: FeeAllocation,
  paidWei: WeiString,
  claims: z.number().int().nonnegative(),
  /** `fundedWei − paidWei` (never negative). */
  unclaimedWei: WeiString,
  fundedTxHash: Hash32.nullable(),
  /** The transaction of the indexed `EpochClosed` log. */
  closeTxHash: Hash32.nullable(),
})
export type TokenEarningsEpoch = z.output<typeof TokenEarningsEpoch>

export const TokenEarningsQuery = z.object({ limit: z.coerce.number().int().min(1).max(104).default(26) })

export const TokenEarnings = z.object({
  address: Address,
  agent: AgentSummary,
  feePolicy: FeePolicy,
  /** FeeDistributor parameters of this deployment (EVM `block.number` units: Ethereum L1 blocks on Robinhood Chain). */
  rules: z.object({
    epochLengthBlocks: z.number().int().positive(),
    /** 0 = holding rule off on this deployment. */
    holdBlocks: z.number().int().nonnegative(),
    /** null when the deployment file does not record it. */
    claimWindowEpochs: z.number().int().positive().nullable(),
    /** Seconds per L1 block used for the time estimates. */
    blockSeconds: z.number().int().positive(),
  }),
  collected: z.array(TokenEarningsCollected),
  /** Per reward token over the listed epochs. */
  totals: z.array(z.object({ rewardToken: Address, fundedWei: WeiString, paidWei: WeiString, allocation: FeeAllocation, epochs: z.number().int().nonnegative() })),
  /**
   * The epoch accruing now: `index` is the next epoch number, `since` the last close (null before any close),
   * `estimatedCloseAt` = since + epoch length (an estimate; `closeEpoch` runs once the L1 block is reached).
   * `accruing` is the holder-eligible fee ledger recorded since the last close, per fee token.
   */
  openEpoch: z.object({
    index: z.number().int().nonnegative(),
    since: IsoTimestamp.nullable(),
    estimatedCloseAt: IsoTimestamp.nullable(),
    accruing: z.array(z.object({ token: Address, amountWei: WeiString, allocation: FeeAllocation })),
  }),
  epochs: z.array(TokenEarningsEpoch),
  asOf: IsoTimestamp,
})
export type TokenEarnings = z.output<typeof TokenEarnings>
